import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HintIcon,
  Input,
} from '@/shared/ui';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Подсказка у значка «?» рядом с подписью. */
  hint?: string;
};

/** Поле формы входа/регистрации: подпись, инпут, сообщение об ошибке. */
export function AuthTextField<T extends FieldValues>({
  control,
  name,
  label,
  type = 'text',
  placeholder,
  autoComplete,
  disabled,
  autoFocus,
  hint,
}: Props<T>) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword && revealed ? 'text' : type;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {hint ? (
            <div className="flex items-center gap-1.5">
              <FormLabel className="text-slate-600">{label}</FormLabel>
              <HintIcon text={hint} />
            </div>
          ) : (
            <FormLabel className="text-slate-600">{label}</FormLabel>
          )}
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                type={inputType}
                placeholder={placeholder}
                autoComplete={autoComplete}
                disabled={disabled}
                autoFocus={autoFocus}
                className="border-slate-200 bg-white text-[15px] text-slate-900 placeholder:text-slate-400 focus:ring-[var(--primary)]/25 disabled:opacity-60"
              />
              {isPassword ? (
                <button
                  type="button"
                  onClick={() => setRevealed((value) => !value)}
                  tabIndex={-1}
                  aria-label={revealed ? 'Скрыть пароль' : 'Показать пароль'}
                  className="absolute inset-y-0 right-3 grid place-items-center text-slate-400 transition-colors hover:text-slate-600"
                >
                  {revealed ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              ) : null}
            </div>
          </FormControl>
          <FormMessage className="text-xs text-red-600" />
        </FormItem>
      )}
    />
  );
}
